import React from 'react';
import viewUtil from '../lib/viewUtil';

const getRoundAndPick = (pickNumber) => ({ round: Math.ceil(pickNumber / 12), pick: pickNumber % 12 || 12 });

/**
 * Builds a projected pick list for all 84 slots.
 * - Completed picks show the actual player selected.
 * - Future picks project the next best available player by Yahoo rank.
 *
 * NOTE: The projection logic here is intentionally simple (best player available
 * in order). Swap out `buildProjections` when a more sophisticated model is ready.
 */
const buildProjections = (draftPicks, draftBoard, playerData) => {
    // draftBoard is already sorted ascending by yahooleaguerank
    const available = [...draftBoard];

    return draftPicks.map((pick, index) => {
        const pickNumber = index + 1;
        const { round, pickInRound } = (() => {
            const r = getRoundAndPick(pickNumber);
            return { round: r.round, pickInRound: r.pick };
        })();

        if (pick.pick) {
            const player = playerData.find(p => p.id === pick.pickId);
            return {
                pickNumber,
                round,
                pickInRound,
                manager: pick.manager,
                playerName: pick.pick,
                posname: player?.posname || '--',
                nflteam: player?.nflteam || '--',
                yahooleaguerank: player?.yahooleaguerank || '--',
                birthdate: player?.birthdate || null,
                isActual: true,
            };
        }

        const projected = available.shift();
        return {
            pickNumber,
            round,
            pickInRound,
            manager: pick.manager,
            playerName: projected?.name || '--',
            posname: projected?.posname || '--',
            nflteam: projected?.nflteam || '--',
            yahooleaguerank: projected?.yahooleaguerank || '--',
            birthdate: projected?.birthdate || null,
            isActual: false,
        };
    });
};

const DraftOrderModal = ({ draftPicks, draftBoard, playerData, managerOnTheClockIndex }) => {
    const projectedPicks = buildProjections(draftPicks, draftBoard, playerData);

    return (
        <div id="draft-order-modal-container" className="modal fade" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content bg-dark text-white border-secondary">
                    <div className="modal-header border-secondary">
                        <div>
                            <h5 className="modal-title mb-0">Draft Order &amp; Projections</h5>
                        </div>
                        <button type="button" className="btn-close btn-close-white ms-3" data-bs-dismiss="modal" aria-label="Close"/>
                    </div>
                    <div className="modal-body p-0">
                        <div className="table-responsive">
                            <table className="table table-dark table-striped table-hover table-sm mb-0">
                                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th>#</th>
                                        <th>Rd</th>
                                        <th>Pk</th>
                                        <th>Manager</th>
                                        <th>Player</th>
                                        <th>Pos</th>
                                        <th>Team</th>
                                        <th>Age</th>
                                        <th>Y!</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projectedPicks.map((pick) => {
                                        const isOnClock = pick.pickNumber - 1 === managerOnTheClockIndex;
                                        return (
                                            <tr
                                                key={`draft-order-row-${pick.pickNumber}`}
                                                className={isOnClock ? 'table-warning' : ''}
                                            >
                                                <td>{pick.pickNumber}</td>
                                                <td>{pick.round}</td>
                                                <td>{pick.pickInRound}</td>
                                                <td>{pick.manager}</td>
                                                <td>{pick.playerName}</td>
                                                <td>{pick.posname}</td>
                                                <td>{pick.nflteam}</td>
                                                <td>{viewUtil.getAge(pick.birthdate) ?? '--'}</td>
                                                <td>{pick.yahooleaguerank}</td>
                                                <td>
                                                    {pick.isActual
                                                        ? <span className="badge bg-success">✓</span>
                                                        : isOnClock
                                                            ? <span className="badge bg-warning text-dark">Clock</span>
                                                            : <span className="badge bg-secondary">Proj</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DraftOrderModal;
